import { PaymentFlowContext, PaymentFlowResult } from '@/Services/PaymentMethod/types';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import AppConfig from '@/AppConfig';
import { mapTaxLocationValidationError } from '@/Services/PaymentMethod/taxLocationError';

/**
 * Processes async payment methods using PaymentIntent flow.
 * Creates subscription first, then confirms payment with mandate for recurring authorization.
 *
 * @param context - The payment flow context
 * @returns PaymentFlowResult indicating success or error
 */
export default async function processAsyncPayment(context: PaymentFlowContext): Promise<PaymentFlowResult> {
  try {
    // Step 1: Create customer and save billing details (if needed)
    await axios.post(AppConfig.getAPIBaseUrl() + '/subscription/stripe/payment-methods', {
      legal_entity: context.stripeCheckoutService.legalEntity,
      customer: context.stripeCheckoutService.customer,
    });

    // Step 2: Create subscription with first invoice
    // Backend creates subscription with payment_behavior=default_incomplete
    // This generates a PaymentIntent for the first invoice
    const response = await axios.post(AppConfig.getAPIBaseUrl() + '/subscription/stripe/', {
      plan_id: context.selectedPlan.id,
      payment_method_type: context.selectedPaymentMethodType,
      is_wallet: false,
    });

    const clientSecret = response.data.client_secret;

    if (!clientSecret) {
      Sentry.captureException(new Error('No client_secret returned from subscription creation'));
      return {
        success: false,
        error: {
          message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
          container: 'payment-element-container',
          error_code: 'ERR-104',
        },
      };
    }

    // Step 3: Confirm payment with mandate data
    // This will redirect to the payment method provider for authorization
    // The mandate_data enables recurring payments for future subscription renewals
    const { error } = await confirmPaymentWithMandate(context, clientSecret);

    if (error) {
      Sentry.captureException(error);

      return {
        success: false,
        error: {
          message_key:
            error.code && error.code !== 'payment_method_provider_decline'
              ? `checkout.form.stripe_errors.${error.code}`
              : 'checkout.checkout_errors.unexpected_payment_processing_error',
          container: 'payment-element-container',
          error_code: 'ERR-112',
        },
      };
    }

    // Success -> the user is already navigating away (to the provider for PayPal/Klarna,
    // or via the QR-modal redirect for PIX) and returns to /processing afterward.
    return { success: true };
  } catch (error: any) {
    // Bad billing address rejected by Stripe Tax validation — a user-fixable 422, not an exception.
    const taxLocationError = mapTaxLocationValidationError(error);
    if (taxLocationError) {
      return { success: false, error: taxLocationError };
    }

    Sentry.captureException(error);
    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-105',
      },
    };
  }
}

/**
 * Confirms a PaymentIntent with mandate data for recurring payments
 * Used for async payment methods that require redirect and mandate acceptance.
 *
 * @param context - The payment flow context
 * @param clientSecret - The PaymentIntent client secret from subscription invoice
 * @returns Object containing error if confirmation failed
 */
async function confirmPaymentWithMandate(context: PaymentFlowContext, clientSecret: string): Promise<{ error: any }> {
  if (!context.stripe || !context.elements) {
    return { error: { message: 'not_ready' } };
  }

  const selectedPlanId = context.selectedPlan.id;
  const selectedPlanName = context.selectedPlan.planName.toLowerCase();

  try {
    const billingAddress = context.stripeCheckoutService.customer?.address;

    const { error } = await context.stripe.confirmPayment({
      elements: context.elements,
      clientSecret,
      confirmParams: {
        return_url: `${AppConfig.getAppUrl()}/processing?checkoutType=${
          context.checkoutType || 'new'
        }&plan=${selectedPlanName}&planId=${selectedPlanId}&interval=${context.selectedPlan.interval}`,
        payment_method_data: {
          billing_details: {
            name: context.stripeCheckoutService.customer?.name,
            email: context.stripeCheckoutService.customer?.email,
            address: {
              line1: billingAddress?.line1,
              line2: billingAddress?.line2,
              city: billingAddress?.city,
              state: billingAddress?.state,
              postal_code: billingAddress?.postal_code,
              country: billingAddress?.country,
            },
          },
        },
        mandate_data: {
          customer_acceptance: {
            type: 'online',
            online: {
              infer_from_client: true,
            },
          },
        },
      },
    });

    return { error };
  } catch (error: any) {
    Sentry.captureException(error);
    return { error };
  }
}
